import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { grantNodeKey, grantRealmNodeKey, isEncryptedNode, KEY_OUTCOME } from '../utils/shareNodeKey';
import './SharedWithRow.css';
import { CircleDot, Box, User } from 'lucide-react';
import { plural } from '../nucleus/format';
import Segmented from './nucleus/Segmented';
import { FindBar } from './nucleus/FacetRail';

/**
 * Who else can reach this node — as a property of the node, next to Ownership.
 *
 * Sharing lived on the Privacy Realms / Exposure Profiles pages because the API
 * could only answer the inverse: a grant, carrying the node ids it covers. To
 * ask "who can see THIS node" you had to read every grant backwards, so the UI
 * sent you to where the grants are. `GET /nodes/<id>/shared-with` answers the
 * question the node has, and this is the surface for it.
 *
 * The distinction that must survive into the UI: a grant reaches a node either
 * because somebody chose that node (`direct`) or because it covers the node's
 * whole schema (`schema`). Ink holding nodes:read on `diary-entry` reaches every
 * diary entry there will ever be. Rendering it beside a friend who was handed
 * one entry, as if those were the same act, is a lie of omission — so `schema`
 * grants are shown, always, and shown as what they are.
 */

/* A glyph for what holds the grant, never letters (DESIGN.md, "Faces"); the
 * name rides the title, never a raw id. */
function glyphOf(kind) {
  const Icon = kind === 'realm' ? CircleDot : kind === 'schema' ? Box : User;
  return <Icon size={12} strokeWidth={1.8} aria-hidden="true" />;
}

/* What kind of thing holds this grant. An automation reading your node is
 * sharing, whatever the consent screen called it, so every type gets a word. */
const ENTITY_WORD = {
  realm: 'realm',
  friend: 'person',
  app: 'app',
  group_member: 'group',
  group_connection: 'group',
  public: 'public',
  automation: 'automation',
  plugin_execution: 'plugin',
  plugin_delegation: 'plugin',
  cosm_agent: 'agent',
};

/* COSM agents get their own tab: they are given nodes and realms in COSM, not
 * here, so beside the realms people share through they read as noise — and in a
 * tab of their own, as the answer to "which agents can read this". */
export const isAgentRow = (row) => row.entity_type === 'cosm_agent';
export const agentsOnly = (g) => g.reaches.length > 0 && g.reaches.every(isAgentRow);

/**
 * How a group of grants reaches this node, in words — the thing the count alone
 * was hiding.
 *
 * "reaches 4 recipients" rendered a friend who was handed one note and four apps
 * that read every note of its kind as the same sentence. The endpoint has always
 * told them apart (`reach: direct | schema | realm`); only the row threw it away,
 * and the person reading it had no way to know that four names meant their whole
 * diary rather than four notes.
 *
 * So the breakdown is the subtitle now: what was GIVEN this node, and what reads
 * everything of its type, counted separately and never merged.
 */
export function reachBreakdown(g, schemaLabel) {
  const of = (kind) => g.reaches.filter(r => r.reach === kind);
  const direct = of('direct');
  const schema = of('schema');
  const realm = of('realm');
  // A count is only worth printing when it distinguishes something. When every
  // grant in the group reaches the same way, the kinds line above already gave
  // the number, and repeating it reads as two different facts.
  const all = (list) => list.length === g.reaches.length;
  const parts = [];

  if (direct.length) parts.push(all(direct) ? 'given this one' : `${direct.length} given this one`);

  if (schema.length) {
    // The load-bearing half, and it is not only reading: an app that holds edit
    // and delete over the schema can change and remove every one of these, which
    // is the fact somebody checking their exposure actually needs.
    const verb = schema.some(r => r.can_edit) ? 'read and change' : 'read';
    parts.push(`${all(schema) ? '' : `${schema.length} `}${verb} every ${schemaLabel}`);
  }

  if (realm.length) parts.push(all(realm) ? 'through a realm' : `${realm.length} through a realm`);

  return parts.length ? parts.join(' · ') : 'not granted to anyone yet';
}

/* What the things holding these grants ARE — "4 apps" says more than "4
 * recipients", and the difference between an app and a person is the whole
 * question when you are checking who can read your diary. */
export function reachKinds(g) {
  const words = g.reaches.map(r => ENTITY_WORD[r.entity_type] || 'recipient');
  const counts = words.reduce((a, w) => ({ ...a, [w]: (a[w] || 0) + 1 }), {});
  return Object.entries(counts)
    .map(([w, c]) => `${c} ${c === 1 ? w : (w === 'person' ? 'people' : `${w}s`)}`)
    .join(', ');
}

function realmTitle(g) {
  if (g.is_public) return `${g.realm_name} — reachable by anyone, not only people you shared with`;
  const who = g.reaches.length ? `${reachKinds(g)} — ${reachBreakdown(g, 'node of this type')}`
    : 'not granted to anyone yet';
  return `${g.realm_name} — ${who}`;
}

/**
 * The bar shows REALMS, not the people in them.
 *
 * Somebody reaches this node BECAUSE a realm grants it to them, so a face for
 * the person and another for the realm said the same grant twice — and the one
 * it put first was the person, which reads as "shared with SH" when what you
 * did was put the node in a realm SH happens to be in. Access is granted and
 * taken away at the realm; that is the thing to name.
 */
export function RealmFaces({ groups, max = 4 }) {
  return (
    <>
      {groups.slice(0, max).map(g => (
        <span key={g.realm_id || 'none'}
              className={`swf swf--realm${g.is_public ? ' swf--public' : ''}`}
              style={g.color && !g.is_public ? { background: g.color, color: '#fff' } : undefined}
              title={realmTitle(g)}>
          {glyphOf('realm')}
        </span>
      ))}
      {groups.length > max && <span className="swf swf--more">+{groups.length - max}</span>}
    </>
  );
}

export function Face({ row, onClick }) {
  const cls = row.reach === 'realm' ? 'realm'
    : row.reach === 'schema' ? 'schema'
    : row.can_edit ? 'edit' : 'view';
  const how = row.reach === 'realm'
    ? `${row.label} — a realm; reaches everyone it is granted to`
    : row.reach === 'schema'
      ? `${row.label} — reaches every node of this schema`
      : `${row.label} — can ${row.can_edit ? 'edit' : 'view'} this node`;
  return (
    <span className={`swf swf--${cls}`} title={how} onClick={onClick} role={onClick ? 'button' : undefined}>
      {glyphOf(row.reach === 'realm' ? 'realm' : row.reach === 'schema' ? 'schema' : 'person')}
    </span>
  );
}

export default function SharedWithRow({ node, schema, onManageRealms }) {
  const [state, setState] = useState({ loading: true, rows: [], groups: [], error: null });
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!node?.id) return;
    setState(s => ({ ...s, loading: true }));
    try {
      const res = await api.request(`/nodes/${node.id}/shared-with`);
      setState({ loading: false, rows: res?.shared_with || [], groups: res?.grouped || [], error: null });
    } catch (err) {
      // Only the owner may ask. A node you are merely a recipient of answers
      // 403, and that is not an error worth shouting about — it is simply not
      // your question to ask.
      setState({ loading: false, rows: [], groups: [], error: err?.status === 403 ? null : err });
    }
  }, [node?.id]);

  useEffect(() => { load(); }, [load]);

  if (state.error) return null;

  const rows = state.rows;
  const direct = rows.filter(r => r.reach === 'direct');
  const viaSchema = rows.filter(r => r.reach === 'schema');
  const viaRealm = rows.filter(r => r.reach === 'realm');

  return (
    <div className="prop-row" data-qh="prop-shared">
      <div className="prop-key" title="Shared with">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="9" cy="8" r="3.2"/><path d="M3 20c0-3.2 2.7-5.5 6-5.5s6 2.3 6 5.5"/>
          <path d="M16 11.2a3.2 3.2 0 0 0 0-6.4M18 20c0-2.2-.9-4-2.4-5"/>
        </svg>
        <span className="prop-key__name">Shared with</span>
      </div>

      <div className="prop-value sw">
        {state.loading ? (
          <span className="prop-empty">…</span>
        ) : state.groups.length === 0 ? (
          /* Not "0 people" and not a badge. Private is the ordinary case and
             should read as nothing at all — but it is still a word, because a
             blank here would read as data that failed to load. */
          <button type="button" className="btn sw__private" onClick={() => setOpen(true)}>
            Private
          </button>
        ) : (
          <button type="button" className="btn sw__faces" onClick={() => setOpen(v => !v)}>
            <RealmFaces groups={state.groups} />
          </button>
        )}

        {open && (
          <SharePopover
            node={node}
            groups={state.groups}
            direct={direct}
            viaSchema={viaSchema}
            viaRealm={viaRealm}
            agents={rows.filter(isAgentRow)}
            schema={schema}
            onChanged={load}
            onClose={() => setOpen(false)}
            onManageRealms={onManageRealms}
          />
        )}
      </div>
    </div>
  );
}

/**
 * The one new surface: who can reach this node, and adding or removing people.
 *
 * Only `direct` grants get a remove button. A grant that reaches this node
 * through its whole schema is not something to undo from one row — narrowing it
 * would change every node of that type — so those are shown and explained, and
 * the change is made in Privacy realms where its blast radius is visible.
 *
 * View↔edit is not offered here either. Widening somebody's standing access is
 * a decision about the relationship, not about the node you happen to have open.
 */
function SharePopover({ node, schema, groups, direct, viaSchema, viaRealm, agents = [], onChanged, onClose, onManageRealms }) {
  // "every diary entry" lands; "every node of this schema" is a shrug.
  const schemaLabel = (schema?.name || schema?.schema_name || node?.schema_name || 'node of this type')
    .replace(/[-_]/g, ' ').toLowerCase();
  useEffect(() => {
    const esc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);


  const [tab, setTab] = useState('people');
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [busy, setBusy] = useState(null);
  const [problem, setProblem] = useState(null);

  /* Debounced, because a search per keystroke against a user directory is a
     request per keystroke. */
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setFound([]); return; }
    const t = setTimeout(() => {
      const src = {
        people: [`/users/search?q=${encodeURIComponent(term)}&limit=6`, 'users', 'friend'],
        groups: ['/groups', 'groups', 'group_member'],
        apps: ['/apps', 'apps', 'app'],
      }[tab];
      if (!src) return;
      const [url, key, entityType] = src;
      api.request(url)
        .then(r => {
          const list = Array.isArray(r) ? r : (r?.[key] || []);
          const named = list
            .map(x => ({
              id: x.user_id || x.id,
              name: x.username || x.name,
              entity_type: entityType,
            }))
            .filter(x => x.id && x.name);
          // /groups and /apps have no server-side search, so filter here.
          setFound(tab === 'people'
            ? named
            : named.filter(x => x.name.toLowerCase().includes(term.toLowerCase())).slice(0, 6));
        })
        .catch(() => setFound([]));
    }, 220);
    return () => clearTimeout(t);
  }, [q, tab]);

  const already = new Set(direct.map(r => r.label.toLowerCase()));

  /* Realms are the other way to answer "who can see this". A person is one
     recipient; a realm is a standing set of them, and sharing a node into one
     reaches everybody it is granted to — now and later. Kept behind a toggle
     because that is the larger act and should be chosen, not stumbled into. */
  const [showRealms, setShowRealms] = useState(false);
  const [realms, setRealms] = useState([]);
  const [realmQ, setRealmQ] = useState('');

  /* Whether a realm already carries this node is a FACT in the payload — its
     ceiling lists the node ids it grants read of. Reading it beats remembering
     what was clicked: a realm added on another tab, or last week, is still
     shown as added. */
  const realmHasNode = (realm) => {
    const scope = ((realm?.ceiling || {}).nodes || {}).read || {};
    return (scope.nodes || []).map(String).includes(String(node.id));
  };
  useEffect(() => {
    if (tab !== 'realms' || realms.length) return;
    api.request('/privacy-realms')
      /* The response key is `profiles` — realms were called privacy profiles and
         the payload kept the name. The old fallback (`r?.realms || r`) therefore
         assigned the whole response OBJECT, and rendering called .map on it.
         Never assign a non-array here: a shape change should render nothing,
         not take the panel down. */
      .then(r => setRealms(Array.isArray(r) ? r : (r?.profiles || r?.realms || [])))
      .catch(() => setRealms([]));
  }, [tab, realms.length]);

  const addToRealm = async (realm) => {
    setBusy(realm.id); setProblem(null);
    try {
      await api.request('/privacy-realms/share-node', {
        method: 'POST',
        body: { node_id: node.id, realm_ids: [realm.id] },
      });

      /* Same rule as sharing with a person: the node is now visible to the
         realm, and for an encrypted node that is not the same as readable. */
      const key = await grantRealmNodeKey({ node, schema, realmId: realm.id, encKeys });
      if (key.outcome !== KEY_OUTCOME.DONE && key.outcome !== KEY_OUTCOME.NOT_ENCRYPTED) {
        setProblem({ partial: true, message: key.message });
      }
      // Re-read so 'Added' comes from the server rather than from optimism.
      api.request('/privacy-realms')
        .then(r => setRealms(Array.isArray(r) ? r : (r?.profiles || r?.realms || [])))
        .catch(() => {});
      onChanged?.();
    } catch (err) {
      setProblem({ message: err?.message || 'That did not work.' });
    }
    setBusy(null);
  };

  const { encKeys } = useAuth();

  const add = async (user, access = 'view') => {
    setBusy(user.id); setProblem(null);
    try {
      const res = await api.request(`/nodes/${node.id}/share`, {
        method: 'POST',
        body: { entity_id: user.id, entity_type: user.entity_type || 'friend', access },
      });
      setQ(''); setFound([]);

      /* A grant makes the node visible; for an encrypted node it does not make
         it readable. Handing over the key is part of sharing, and when it can
         not happen the person is told — otherwise the share looks like a
         success and the recipient opens ciphertext. */
      // Key wrapping is per-PERSON: it wraps to a recipient's public key, and a
      // group or app holds no such key here. Those read encrypted content
      // through their realm's key, which the realm path handles.
      if ((user.entity_type || 'friend') === 'friend') {
        const ep = (res?.shared_with || []).find(r => r.reach === 'direct' && r.label === user.name);
        const key = await grantNodeKey({
          node, schema, recipientUserId: user.id, epId: ep?.ep_id, encKeys,
        });
        if (key.outcome !== KEY_OUTCOME.DONE && key.outcome !== KEY_OUTCOME.NOT_ENCRYPTED) {
          setProblem({ partial: true, message: key.message });
        }
      } else if (isEncryptedNode(node, schema)) {
        /* A group or app holds no personal public key to wrap to, so the share
           grants the node without handing over anything to read it with. Saying
           so beats the alternative: a share that looks like it worked and opens
           as ciphertext. */
        setProblem({ locked: true, message:
          `${user.name} can now see this node listed, but its contents are encrypted and `
          + 'stay unreadable until the realm it went into has an encryption key.' });
      }
      onChanged?.();
    } catch (err) {
      setProblem({ message: err?.message || 'That did not work.' });
    }
    setBusy(null);
  };

  const remove = async (row) => {
    setBusy(row.ep_id); setProblem(null);
    try {
      await api.request(`/nodes/${node.id}/share/${row.ep_id}`, { method: 'DELETE' });
      onChanged?.();
    } catch (err) {
      setProblem({ message: err?.message || 'That did not work.' });
    }
    setBusy(null);
  };

  return (
    <>
      <div className="sw-scrim" onClick={onClose} />
      <div className="sw-pop" role="dialog" aria-label="Who can see this node">
        <div className="sw-pop__head">
          <h4>Realms with access</h4>
          <span>
            {groups.length === 0
              ? 'none'
              : plural(groups.length, 'realm')}
          </span>
        </div>

        <div className="sw-pop__body">
          {isEncryptedNode(node, schema) && (
            <p className="sw-note sw-note--lock">
              This node is encrypted. Sharing it also hands the recipient its key, in your
              browser — they can read it only if they have set up encryption.
            </p>
          )}

          {/* Groups and apps are deliberately absent, not stubbed: POST
              /nodes/<id>/share resolves entity_type='friend' only, and an app's
              grant is its authorization — not obviously the owner's to extend
              from a node row. A tab that looked live and did nothing would be
              worse on a control that governs who reads your data. */}
          <Segmented
            label="Share with" fill className="sw-tabs" value={tab}
            onChange={(k) => { setTab(k); setQ(''); setRealmQ(''); }}
            options={[{ id: 'people', label: 'People' }, { id: 'groups', label: 'Groups' }, { id: 'apps', label: 'Apps' }, { id: 'agents', label: 'Agents' }, { id: 'realms', label: 'Realms' }]}
          />

          {tab !== 'agents' && <FindBar
            compact className="sw-find"
            value={tab === 'realms' ? realmQ : q}
            onChange={(v) => (tab === 'realms' ? setRealmQ : setQ)(v)}
            placeholder={{ realms: 'Filter realms…', groups: 'Find a group…', apps: 'Find an app…' }[tab] || 'Add someone by name…'}
            label={tab === 'realms' ? 'Filter realms' : 'Find a person to share with'}
          />}

          {tab !== 'realms' && tab !== 'agents' && found.length > 0 && (
            <div className="sw-found">
              {found.map(u => (
                <div className="sw-rec" key={u.id}>
                  <Face row={{ label: u.name, reach: 'direct', can_edit: false }} />
                  <span className="sw-rec__id">
                    <span className="sw-rec__name">{u.name}</span>
                    <span className="sw-rec__sub">
                      {already.has(String(u.name).toLowerCase())
                        ? 'already has this node'
                        : (ENTITY_WORD[u.entity_type] || 'recipient')}
                    </span>
                  </span>
                  {!already.has(String(u.name).toLowerCase()) && (
                    <button type="button" className="btn sw-add" disabled={busy === u.id}
                            onClick={() => add(u)}>
                      {busy === u.id ? '…' : 'Share'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {problem && (
            <p className={`sw-note${problem.locked ? ' sw-note--lock'
              : problem.handoff || problem.partial ? ' sw-note--handoff' : ' sw-note--bad'}`}>
              {problem.message}
            </p>
          )}

          {/* Grouped by realm, because that is how access is actually built:
              every EP hangs off one, and a person-share beyond the picked
              realms mints a shared realm named for them. Listing "people" and
              "realms" as rivals described the plumbing, not the model. */}
          {tab === 'agents' && (
            <div className="sw-agents">
              {agents.length === 0 && <p className="sw-note sw-note--plain">No agent can read this node.</p>}
              {agents.map(row => (
                <Recipient key={row.ep_id} row={row} busy={busy === row.ep_id}
                           onRemove={row.reach === 'direct' ? () => remove(row) : undefined} />
              ))}
            </div>
          )}

          {tab !== 'realms' && tab !== 'agents' && groups.filter(g => !agentsOnly(g)).map(g => (
            <div className={`sw-group${g.is_public ? ' sw-group--public' : ''}`} key={g.realm_id || 'none'}>
              {/* The realm alone. Listing its members underneath repeated, per
                  realm, what the realm already says — and the members are not
                  the thing you act on here: access is granted and taken away at
                  the realm, which is where the count leads. */}
              <span className="swf swf--realm"
                    style={g.color ? { background: g.color, color: '#fff' } : undefined}
                    title={g.realm_name}>
                {glyphOf('realm')}
              </span>
              <span className="sw-rec__id">
                <span className="sw-rec__name">{g.realm_name}</span>
                <span className="sw-rec__sub">
                  {g.reaches.length ? reachKinds(g) : 'not granted to anyone yet'}
                  {g.grants_node && ' · grants this node'}
                </span>
                {/* The count was the whole message and it was the wrong one.
                    What reads EVERY node of this type is the line that decides
                    whether you should be worried, so it gets its own row and
                    its own weight. */}
                {g.reaches.length > 0 && (
                  <span className={`sw-rec__reach${
                    g.reaches.some(r => r.reach === 'schema') ? ' sw-rec__reach--schema' : ''}`}>
                    {reachBreakdown(g, schemaLabel)}
                  </span>
                )}
              </span>
              {g.is_public && (
                <span className="sw-group__public" title="Reachable by anyone, not only people you have shared with">
                  Anyone
                </span>
              )}
            </div>
          ))}

          {/* The bucket's name describes the filing, not the access. Anyone
              reading it while worried deserves the sentence, not the label. */}
          {tab !== 'realms' && tab !== 'agents' && groups.some(g => !g.realm_id) && (
            <p className="sw-note sw-note--plain">
              “Not in a realm” means those grants reach this node without going through
              a realm — an app you authorised, or an agent acting for you. Access like that
              is changed where the app was granted, not here.
            </p>
          )}

          {tab !== 'realms' && tab !== 'agents' && groups.length === 0 && (
            <p className="sw-note sw-note--plain">
              No realm gives access to this node, so nobody else can reach it. Access is granted
              by adding it to a realm.
            </p>
          )}

          {/* Manage belongs to the realm section, not to the popover. Shown by
              default it was a second exit sitting beside the primary action,
              competing with it before realms were even on screen. */}
          <div className="sw-pop__foot">

          </div>

          {tab === 'realms' && (
            <div className="sw-realms">
              <p className="sw-note">
                A realm is a standing set of recipients. Adding this node reaches everyone the
                realm is granted to — including anyone added to it later.
              </p>
              {(() => {
                const term = realmQ.trim().toLowerCase();
                const list = realms.filter(r => !term || String(r.name || '').toLowerCase().includes(term));
                if (!list.length) {
                  return <p className="sw-note sw-note--plain">
                    {realms.length ? 'No realm matches that.' : 'No realms yet.'}
                  </p>;
                }
                return (
                  <div className="sw-realms__list">
                    {list.map(r => {
                      const has = realmHasNode(r);
                      return (
                        <div className="sw-rec" key={r.id}>
                          <span className="sw-rec__id">
                            <span className="sw-rec__name">{r.name}</span>
                            <span className="sw-rec__sub">realm</span>
                          </span>
                          {has ? (
                            <span className="sw-added">Added</span>
                          ) : (
                            <button type="button" className="btn sw-add" disabled={busy === r.id}
                                    onClick={() => addToRealm(r)}>
                              {busy === r.id ? '…' : 'Add'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              <button type="button" className="btn sw-pop__link sw-realms__manage"
                      onClick={() => { onClose(); onManageRealms?.(); }}>
                Manage in Realms →
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Recipient({ row, busy, onRemove }) {
  const word = ENTITY_WORD[row.entity_type] || row.entity_type;
  return (
    <div className="sw-rec">
      <Face row={row} />
      <span className="sw-rec__id">
        <span className="sw-rec__name">{row.label}</span>
        <span className="sw-rec__sub">
          {word}
          {row.reach === 'realm' && (
            row.reaches_count > 0
              ? ` · reaches ${plural(row.reaches_count, 'grant')}`
              : ' · not granted to anyone yet'
          )}
          {/* How much else this grant carries. Adding to it is widening a
              standing relationship, not making an isolated share. */}
          {row.reach === 'direct' && row.also_reaches_nodes > 1 &&
            ` · also sees ${row.also_reaches_nodes - 1} other ${row.also_reaches_nodes - 1 === 1 ? 'node' : 'nodes'}`}
          {row.reach === 'schema' && row.also_reaches_schemas > 1 &&
            ` · ${row.also_reaches_schemas} schemas`}
        </span>
      </span>
      <span className={`sw-cap sw-cap--${row.can_edit ? 'edit' : 'view'}`}>
        {row.can_edit ? 'Edit' : 'View'}
      </span>
      {onRemove && (
        <button type="button" className="btn sw-x" onClick={onRemove} disabled={busy}
                title={`Remove this node from ${row.label}'s access`}>
          {busy ? '…' : '✕'}
        </button>
      )}
    </div>
  );
}
